import { computed } from "vue";
import { RequestStateType } from "@/custom-types/GeneralTypes";
import { Tender } from "@/stores/tender/types";

type TenderStateProps = {
  tender: Tender;
  supplierId: number;
};

// Constants for state groupings
const REJECTED_STATES = [
  RequestStateType.REJECTED,
  RequestStateType.ARCHIVED,
  RequestStateType.UNANSWERED,
  RequestStateType.DELETED,
] as const;

const COMPLETED_OR_ACCEPTED_STATES = [
  RequestStateType.COMPLETED,
  RequestStateType.ACCEPTED,
  RequestStateType.NOTICE_PERIOD,
] as const;

export const useTenderState = (props: TenderStateProps) => {
  // Core computed properties
  const supplierRequest = computed(() =>
    props.tender?.priceRequests?.[0]?.supplierRequests.find(
      (request) => request.supplierId === props.supplierId,
    ),
  );

  const requestState = computed(() => {
    const state = supplierRequest.value?.requestState?.toLowerCase();
    return state as RequestStateType | undefined;
  });

  // Helper functions
  const isInState = (state: RequestStateType): boolean => {
    return requestState.value === state.toLowerCase();
  };

  const isInAnyState = (states: readonly RequestStateType[]): boolean => {
    if (!requestState.value) return false;
    return states.some((state) => state.toLowerCase() === requestState.value);
  };

  // State checking computed properties
  const isRejected = computed(() => isInAnyState(REJECTED_STATES));

  const isCompletedOrAccepted = computed(() =>
    isInAnyState(COMPLETED_OR_ACCEPTED_STATES),
  );

  const isAccepted = computed(() => isInState(RequestStateType.ACCEPTED));

  const hasActiveAgreement = computed(
    () => isAccepted.value || isInState(RequestStateType.NOTICE_PERIOD),
  );

  const isTenderCompleted = computed(() =>
    isInState(RequestStateType.COMPLETED),
  );

  const isTenderLost = computed(() => isInState(RequestStateType.LOST));

  // Button visibility computed properties
  const showOfferButton = computed((): boolean => {
    if (!props.tender) return false;
    if (isTenderLost.value) return false;
    if (hasActiveAgreement.value) return false;
    if (isRejected.value) return false;
    if (isTenderCompleted.value) return false;
    if (isCompletedOrAccepted.value) return false;

    return true;
  });

  const showMarkAsDoneButton = computed(
    (): boolean => isCompletedOrAccepted.value,
  );

  const showRejectPriceRequestButton = computed((): boolean => {
    if (!requestState.value) return false;

    // Cannot reject if tender is completed or lost
    if (isTenderCompleted.value || isTenderLost.value) return false;

    // Cannot reject if already in offered state
    if (isInState(RequestStateType.OFFERED)) return false;

    // Cannot reject if already rejected or completed/accepted
    return !isRejected.value && !isCompletedOrAccepted.value;
  });

  const showCreatePriceRequestButton = computed((): boolean =>
    Boolean(props.tender && isCompletedOrAccepted.value),
  );

  const showMoveToPriceRequestedButton = computed((): boolean =>
    isInState(RequestStateType.REJECTED),
  );

  return {
    // Core data
    supplierRequest,

    // State checks
    isAccepted,
    hasActiveAgreement,
    isTenderCompleted,
    isTenderLost,
    isRejected,
    isCompletedOrAccepted,

    // Button visibility
    showOfferButton,
    showMarkAsDoneButton,
    showRejectPriceRequestButton,
    showCreatePriceRequestButton,
    showMoveToPriceRequestedButton,
  };
};
